import { useEffect } from 'react';
import ErrorFallback from '@/components/ErrorFallback';
import NotAllowed from '@/modules/core/pages/not-allowed';
import NotFound from '@/modules/core/pages/not-found';
import * as Sentry from '@sentry/react';

export class AuthorizationError extends Error {
  constructor(message = 'User is not an admin') {
    super(message);
    this.name = 'AuthorizationError';
  }
}

export class NotFoundError extends Error {
  constructor(message = 'Resource not found') {
    super(message);
    this.name = 'NotFoundError';
  }
}

interface RouteErrorHandlerProps {
  error: Error;
  reset: () => void;
  routeName: string;
}

export const RouteErrorHandler = ({ error, reset, routeName }: RouteErrorHandlerProps) => {
  useEffect(() => {
    const isExpectedError = error instanceof AuthorizationError || error instanceof NotFoundError;
    if (!isExpectedError) {
      Sentry.captureException(error, {
        tags: {
          route: routeName,
          errorType: 'route-error',
        },
      });
    }
  }, [error, routeName]);

  if (error instanceof AuthorizationError) {
    return <NotAllowed />;
  }

  if (error instanceof NotFoundError) {
    return <NotFound />;
  }

  return <ErrorFallback error={error} resetErrorBoundary={reset} />;
};
